import { InfoCircle, User01 } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import moment from 'moment';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { Callout, Decorator, Spinner } from '@myhealth-raccoon/components';
import { validation } from '@myhealth-raccoon/forms';

import { BookingInformationForm, PatientType, filterAppointmentTypesForClinic } from '.';
import { ProviderOption } from '../elements/ProviderOption';
import { AppointmentTypeEligibilityTerms } from 'src/components/AppointmentTypeEligibilityTerms.tsx';
import { PopoverTooltip } from 'src/components/PopoverTooltip';
import { AppointmentTypeOption, CheckboxItem, RadioList, RadioListRef } from 'src/components/forms/elements';
import { VisitInfo } from 'src/services/auth';
import { AppointmentType, FirstAvailableModel } from 'src/services/availability';
import { BookingUserProfileModel as Doctor } from 'src/services/booking';
import { isExpressTelehealth } from 'src/utils/appointmentTypes';
import { doctorAvatarSrc, doctorName } from 'src/utils/name';
import { PerformConflictChecks } from 'src/utils/validateAppointmentType';

type Props = {
    className?: string;
    appointmentTypes: AppointmentType[];
    onChangeAppointmentType?(appointmentTypeId: string): void;
    doctors: Doctor[];
    onNewValues?(values: BookingInformationForm): void;
    onValidate?(valid: boolean): void;
    defaultValues?: Partial<BookingInformationForm>;
    firstAvailable?: FirstAvailableModel[];
    isLoadingFirstAvailable?: boolean;
    isLoadingAppointmentTypes?: boolean;
    isLoadingDoctors?: boolean;
    patient?: {
        displayName: string;
        dob: string;
        visitInfo: VisitInfo;
    };
    onEditDetails?(): void;
    onBookAsSomeoneElse?(): void;
};

function isExpressTelehealthDisabled() {
    return import.meta.env.VITE_DISABLE_EXPRESS_TELEHEALTH === 'true';
}

export interface WidgetFormRef {
    openAppointmentTypeSelection(): void;
}

function filterDoctors(doctors: Doctor[], appointmentType: AppointmentType | undefined, patientType: PatientType) {
    const docs = doctors.filter(
        (doc) =>
            !appointmentType?.appointmentTypeId ||
            (doc.apptTypeIds.includes(Number(appointmentType.appointmentTypeId)) &&
                (patientType === 'existing' || !doc.noNewPatient))
    );
    if (
        docs.length <= 1 ||
        appointmentType?.displayOnly === '1' ||
        (appointmentType && isExpressTelehealth(appointmentType))
    ) {
        return docs;
    }
    return [
        {
            userId: 'any',
            automedDisplayName: 'Any Provider',
            firstname: '',
            surname: '',
            sortOrder: -1,
            automedProfileUrl: '',
            umbracoProfileUrl: '',
        } as Doctor,
        ...docs,
    ];
}

const emptyArray: unknown[] = [];
export const BookWithClinicForm = forwardRef<WidgetFormRef, Props>(
    (
        {
            className,
            appointmentTypes = emptyArray as AppointmentType[],
            doctors = emptyArray as Doctor[],
            onNewValues,
            onValidate,
            defaultValues,
            firstAvailable,
            isLoadingFirstAvailable,
            isLoadingAppointmentTypes,
            isLoadingDoctors,
            onChangeAppointmentType,
            patient,
            onEditDetails,
            onBookAsSomeoneElse,
        },
        ref
    ) => {
        const appointmentTypeSelectorRef = useRef<RadioListRef>(null);
        const providerSelectorRef = useRef<RadioListRef>(null);

        const {
            handleSubmit: innerSubmit,
            register,
            formState: { errors },
            watch,
            setValue,
            reset,
            control,
        } = useForm<BookingInformationForm>({
            defaultValues,
            shouldFocusError: false,
        });

        const patientType = watch('patientType');
        const appointmentMode = watch('appointmentMode');
        const appointmentTypeId = watch('appointmentTypeId');
        const doctorId = watch('doctorId');

        const filteredAppointmentTypes = useMemo(
            () =>
                patient
                    ? filterAppointmentTypesForClinic(
                          appointmentTypes,
                          patient.visitInfo.isNewPatient ? 'new' : 'existing',
                          appointmentMode
                      ).filter(
                          (apt) =>
                              PerformConflictChecks({
                                  ...patient.visitInfo,
                                  patientDob: patient.dob,
                                  appointmentType: apt,
                                  bannedLevel: undefined,
                              }).valid
                      )
                    : filterAppointmentTypesForClinic(appointmentTypes, patientType, appointmentMode),
            [patient, appointmentTypes, appointmentMode, patientType]
        );

        const selectedAppointmentType = useMemo(
            () => filteredAppointmentTypes.find((apt) => apt.appointmentTypeId === appointmentTypeId),
            [filteredAppointmentTypes, appointmentTypeId]
        );

        const filteredDoctors = useMemo(
            () =>
                (patient
                    ? filterDoctors(
                          doctors,
                          selectedAppointmentType,
                          patient.visitInfo.isNewPatient ? 'new' : 'existing'
                      ).filter(
                          (dr) =>
                              PerformConflictChecks({
                                  ...patient.visitInfo,
                                  patientDob: patient.dob,
                                  appointmentType: selectedAppointmentType,
                                  doctor: dr,
                                  bannedLevel: undefined,
                              }).valid
                      )
                    : filterDoctors(doctors, selectedAppointmentType, patientType)
                ).sort((docA, docB) => {
                    if (docA.sortOrder !== docB.sortOrder) {
                        return docA.sortOrder - docB.sortOrder;
                    }
                    return docA.surname > docB.surname ? 1 : docA.surname < docB.surname ? -1 : 0;
                }),
            [patient, doctors, selectedAppointmentType, patientType]
        );

        const selectedDoctor = useMemo(
            () => filteredDoctors.find((doc) => doc.userId === doctorId),
            [filteredDoctors, doctorId]
        );

        useEffect(() => {
            if (defaultValues) {
                reset(defaultValues);
            }
        }, [defaultValues, reset]);

        useEffect(() => {
            if (patient) {
                setValue('patientType', patient.visitInfo.isNewPatient ? 'new' : 'existing');
            }
        }, [patient, setValue]);

        useEffect(() => {
            if (patientType === 'new' && appointmentMode === 'express telehealth') {
                setValue('appointmentMode', 'faceToFace');
            }
        }, [patientType, appointmentMode, setValue]);

        useImperativeHandle(
            ref,
            () => ({
                openAppointmentTypeSelection: () => appointmentTypeSelectorRef.current?.openSelection(),
            }),
            []
        );

        useEffect(() => {
            // if the currently selected appointment type doesn't match filters
            if (
                appointmentTypeId &&
                filteredAppointmentTypes?.length &&
                !filteredAppointmentTypes.find((a) => a.appointmentTypeId === appointmentTypeId)
            ) {
                setValue(
                    'appointmentTypeId',
                    filteredAppointmentTypes.length === 1 ? filteredAppointmentTypes[0].appointmentTypeId : ''!
                );
                appointmentTypeSelectorRef.current?.expand();
            }
        }, [selectedAppointmentType, filteredAppointmentTypes, setValue, appointmentTypeId, appointmentTypes?.length]);

        useEffect(() => {
            onChangeAppointmentType?.(appointmentTypeId);
        }, [appointmentTypeId, onChangeAppointmentType]);

        useEffect(() => {
            // if the currently selected doctor isn't available
            if (!doctorId && filteredDoctors?.find((x) => x.userId === 'any')) {
                setValue('doctorId', 'any');
                providerSelectorRef.current?.expand();
            } else if (filteredDoctors?.length && !filteredDoctors.find((d) => d.userId === doctorId)) {
                setValue('doctorId', filteredDoctors.length === 1 ? filteredDoctors[0].userId : null!);
                providerSelectorRef.current?.collapse();
            }
        }, [setValue, filteredDoctors, doctorId]);

        useLayoutEffect(() => {
            onValidate?.(!!selectedDoctor && !!selectedAppointmentType);
        }, [selectedDoctor, selectedAppointmentType, onValidate]);

        useEffect(() => {
            if (onNewValues) {
                innerSubmit(onNewValues)();
            }
        }, [patientType, appointmentMode, appointmentTypeId, doctorId, onNewValues, innerSubmit]);

        return (
            <form className={classNames('', className)}>
                {patient ? (
                    <div className="bg-gray-25 p-4">
                        <div className="flex flex-row flex-wrap items-start gap-x-4 gap-y-3">
                            <div className="mr-auto flex w-full flex-col items-start gap-1 sm:w-auto">
                                <span className="text-lg font-semibold text-gray-900">{patient.displayName}</span>
                                <span className="text-base font-medium text-gray-500">
                                    DOB: {moment(patient.dob).format('DD/MM/YYYY')} •{' '}
                                    {patient.visitInfo.isNewPatient ? 'New patient' : 'Existing patient'}
                                </span>
                                <div className="text-success-600 mt-2 font-semibold">
                                    Updated selections below based on your details
                                </div>
                            </div>
                            {onEditDetails && (
                                <button
                                    type="button"
                                    onClick={onEditDetails}
                                    className="text-primary font-semibold hover:underline disabled:pointer-events-none disabled:text-gray-400"
                                >
                                    Edit details
                                </button>
                            )}
                            {onBookAsSomeoneElse && (
                                <button
                                    type="button"
                                    onClick={onBookAsSomeoneElse}
                                    className="font-semibold text-gray-600 hover:underline disabled:pointer-events-none disabled:text-gray-400"
                                >
                                    Book for someone else
                                </button>
                            )}
                        </div>
                        <input
                            type="hidden"
                            {...register('patientType', { value: patient.visitInfo.isNewPatient ? 'new' : 'existing' })}
                        />
                    </div>
                ) : (
                    <Decorator
                        fieldset
                        fieldError={errors.patientType}
                        className="my-4 px-4"
                        label={
                            <span className="text-lg font-normal text-gray-600">
                                Patient Type:{' '}
                                <strong className="text-gray-700">
                                    {patientType === 'existing' && 'Existing patient'}
                                    {patientType === 'new' && 'New patient'}
                                </strong>
                            </span>
                        }
                    >
                        <div className="flex flex-row gap-3">
                            <CheckboxItem
                                {...register('patientType')}
                                type="radio"
                                id="patientType_existing"
                                value="existing"
                            >
                                Existing patient
                            </CheckboxItem>
                            <CheckboxItem {...register('patientType')} type="radio" id="patientType_new" value="new">
                                <span className="hidden sm:flex">New patient at this centre</span>
                                <span className="sm:hidden">New patient</span>
                            </CheckboxItem>
                        </div>
                    </Decorator>
                )}
                <hr />
                <Decorator
                    fieldset
                    fieldError={errors.appointmentMode}
                    className="my-4 px-4"
                    label={
                        <span className="text-lg font-normal text-gray-600">
                            Service Type:{' '}
                            <strong className="text-gray-700">
                                {appointmentMode === 'faceToFace' && 'Face to Face'}
                                {appointmentMode === 'telehealth' && 'Telehealth'}
                                {appointmentMode === 'express telehealth' && 'Express Telehealth'}
                            </strong>
                        </span>
                    }
                >
                    <div className="flex flex-col gap-4 sm:flex-row">
                        <CheckboxItem
                            {...register('appointmentMode')}
                            type="radio"
                            id="appointmentMode_faceToFace"
                            value="faceToFace"
                        >
                            Face to Face
                            <p className="text-xs font-normal text-gray-600">
                                Consult with a healthcare provider in person at the clinic.
                            </p>
                        </CheckboxItem>
                        <CheckboxItem
                            {...register('appointmentMode')}
                            type="radio"
                            id="appointmentMode_telehealth"
                            value="telehealth"
                        >
                            Telehealth
                            <p className="text-xs font-normal text-gray-600">
                                Consult with a healthcare provider via phone or a video call.
                            </p>
                        </CheckboxItem>
                        {!isExpressTelehealthDisabled() && appointmentTypes.find((apt) => isExpressTelehealth(apt)) && (
                            <div className="relative mt-3 flex flex-1 sm:mt-0">
                                <CheckboxItem
                                    {...register('appointmentMode')}
                                    type="radio"
                                    id="appointmentMode_expressTelehealth"
                                    value="express telehealth"
                                    disabled={patientType === 'new'}
                                >
                                    Express Telehealth
                                    <p className="text-xs font-normal text-gray-600">
                                        Bulk-billed, on-demand service for specific quick issues.
                                    </p>
                                </CheckboxItem>
                                <PopoverTooltip
                                    className="bg-success-25 text-success-800 hover:border-success-500 absolute -top-1 left-2 flex -translate-y-1/2 flex-row items-center gap-1 rounded-md border border-gray-300 px-2 py-0.5 text-sm font-bold transition-colors disabled:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:opacity-40 md:left-4 md:top-0"
                                    panelClassname="text-sm"
                                    placement="top"
                                    // approx height of sticky header
                                    pt={130}
                                    disabled={patientType === 'new'}
                                    content={
                                        <>
                                            <h6 className="font-semibold text-gray-700">For Medicare Card Holders</h6>
                                            <p className="text-balance">
                                                This appointment is fully covered by Medicare for eligible Medicare card
                                                holders. No out-of-pocket cost applies.
                                            </p>
                                        </>
                                    }
                                >
                                    Bulk Billing
                                    <InfoCircle className="size-3.5" />
                                </PopoverTooltip>
                            </div>
                        )}
                    </div>
                    {appointmentMode === 'express telehealth' && (
                        <div className="prose prose-sm mt-4 leading-snug">
                            <p>
                                Express {'<'}10 mins Telehealth bulk-billed appointments, on-demand service for eligible
                                patients.{' '}
                                {/* TODO: link to learn more
                            <a target="_blank" href="#!">
                                Learn more
                            </a> */}
                            </p>
                        </div>
                    )}
                </Decorator>

                <hr className="my-4" />

                {!isLoadingAppointmentTypes ? (
                    <>
                        <RadioList
                            className="px-4"
                            display="show-more"
                            fieldError={errors.appointmentTypeId}
                            stringValue={selectedAppointmentType?.appointmentName}
                            label="Appointment Type"
                            items={filteredAppointmentTypes}
                            rules={{ validate: validation.required('Please select an appointment type') }}
                            control={control}
                            name="appointmentTypeId"
                            value={appointmentTypeId}
                            getItemValue={(apt) => apt.appointmentTypeId}
                            render={(apt) => <AppointmentTypeOption appointmentType={apt} />}
                            id="appointment_type"
                            ref={appointmentTypeSelectorRef}
                            size="lg"
                        />
                        {selectedAppointmentType && (
                            <AppointmentTypeEligibilityTerms
                                className="m-4"
                                appointmentType={selectedAppointmentType}
                            />
                        )}
                    </>
                ) : (
                    <Spinner className="mx-auto w-fit" />
                )}
                {filteredAppointmentTypes.length === 0 &&
                    ((appointmentMode === 'telehealth' || appointmentMode === 'express telehealth') &&
                    patientType === 'new' ? (
                        <div className="px-4">
                            <Callout color="error" className="font-medium">
                                GP Telehealth services are only available to existing patients who've had a face-to-face
                                visit in the past 12 months.
                            </Callout>
                        </div>
                    ) : (
                        <div className="px-4">
                            <Callout color="error" className="font-medium">
                                There are no appointment types available for your selections.
                            </Callout>
                        </div>
                    ))}
                {/* TODO until we get additional providers hide the provider selection for express */}
                <hr
                    className={classNames('my-4', {
                        hidden: !selectedAppointmentType || isExpressTelehealth(selectedAppointmentType),
                    })}
                />
                <div
                    className={classNames({
                        hidden: !selectedAppointmentType || isExpressTelehealth(selectedAppointmentType),
                    })}
                >
                    {!isLoadingDoctors ? (
                        <RadioList
                            className="px-4"
                            fieldError={errors.doctorId}
                            stringValue={selectedDoctor && doctorName(selectedDoctor)}
                            label="Provider"
                            items={filteredDoctors}
                            rules={{ validate: validation.required('Please select a provider') }}
                            control={control}
                            name="doctorId"
                            value={doctorId}
                            getItemValue={(apt) => apt.userId}
                            size="lg"
                            ref={providerSelectorRef}
                            closeOnChange
                            subtitle={
                                selectedDoctor && (
                                    <>
                                        <span className="mr-1 inline-block size-6 overflow-hidden rounded-full bg-gray-100 align-text-top text-gray-400 ring-1 ring-gray-200">
                                            {doctorAvatarSrc(selectedDoctor) ? (
                                                <img
                                                    className="size-full object-cover"
                                                    src={doctorAvatarSrc(selectedDoctor)}
                                                />
                                            ) : (
                                                <User01 className="m-1 size-4" />
                                            )}
                                        </span>
                                        {doctorName(selectedDoctor)}
                                    </>
                                )
                            }
                            render={(dr) => {
                                const firstAvailableSingle =
                                    dr.userId === 'any'
                                        ? firstAvailable?.[0]
                                        : firstAvailable?.find(
                                              (f) => f.user.userId.toString() === dr.userId && f.firstAvailable
                                          );
                                return (
                                    <ProviderOption
                                        provider={dr}
                                        firstAvailable={firstAvailableSingle}
                                        isLoadingFirstAvailable={isLoadingFirstAvailable}
                                        appointmentTypeDisplayOnly={
                                            selectedAppointmentType &&
                                            selectedAppointmentType.displayOnly === '1' &&
                                            !isExpressTelehealth(selectedAppointmentType)
                                        }
                                    />
                                );
                            }}
                            id="provider"
                        />
                    ) : (
                        <Spinner className="mx-auto w-fit" />
                    )}
                </div>
            </form>
        );
    }
);
